import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import AddClothingItemModal from "../components/AddClothingItemModal";
import ClothingItemDetail from "./ClothingItemDetail";
import Spinner from "../components/Spinner";
import {useDocumentTitle} from '../hooks/useDocumentTitle';

interface ClothingItemEditData {
    id: string;
    name: string;
    description?: string | null;
    categoryId?: string | null;
    brandId?: string | null;
    sizeId?: string | null;
    conditionId?: string | null;
    colorIds?: string[] | null;
    layerIds?: string[] | null;
    warmthLevelId?: string | null;
    priceRub?: number | null;
    waterProof?: boolean | null;
    purchaseDate?: string | null;
    disposalDate?: string | null;
    thrownAway?: boolean | null;
    seasonIds?: string[] | null;
    materialIds?: string[] | null;
    occasionIds?: string[] | null;
    styleIds?: string[] | null;
    weatherSuitabilityIds?: string[] | null;
    defectIds?: string[] | null;
    photoUrls?: string[] | null;
    purpose?: import('../support/itemLifecycle').ItemPurpose | null;
}

const ClothingItemEdit: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [item, setItem] = useState<ClothingItemEditData | null>(null);
    // До загрузки — имя раздела, после — «Имя вещи».
    useDocumentTitle(item?.name);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchItem = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<ClothingItemEditData>(`/clothing/items/${id}`);
            setItem(res.data);
        } catch (err) {
            console.error('Error fetching clothing item:', err);
            setError('Не удалось загрузить данные');
            setItem(null);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) fetchItem();
    }, [id, fetchItem]);

    const handleSuccess = useCallback(() => {
        navigate(`/clothing/${id}`);
    }, [navigate, id]);

    const handleClose = useCallback(() => {
        navigate(`/clothing/${id}`);
    }, [navigate, id]);

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <Spinner/>
            </div>
        );
    }

    if (error || !item) {
        return (
            <div className="space-y-4">
                <p className="text-red-600 dark:text-red-400">{error ?? 'Вещь не найдена'}</p>
                <button onClick={() => navigate('/clothing')} className="btn-secondary">
                    К списку
                </button>
            </div>
        );
    }

    return (
        <div className="relative">
            <ClothingItemDetail/>
            <div
                className="fw-overlay fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-auto"
                onClick={handleClose}
            >
                <div
                    className="w-full max-w-2xl"
                    onClick={e => e.stopPropagation()}
                >
                    <AddClothingItemModal
                        isOpen={true}
                        editItem={item}
                        renderAsPage={true}
                        onClose={handleClose}
                        onSuccess={handleSuccess}
                    />
                </div>
            </div>
        </div>
    );
};

export default ClothingItemEdit;
